import { classNames } from '../../../shared/components/habita';

export type GoalPaceStatus = 'attention' | 'critical' | 'on-track';

export interface GoalPace {
  actualPercent: number;
  differenceFromExpectedPct: number | null;
  elapsedWorkingDays: number;
  expectedPercent: number;
  expectedValue: number;
  remaining: number;
  status: GoalPaceStatus;
  totalWorkingDays: number;
}

const paceStatusConfig: Record<GoalPaceStatus, {
  dotClassName: string;
  label: string;
  surfaceClassName: string;
}> = {
  attention: {
    dotClassName: 'bg-amber-500',
    label: 'Atenção',
    surfaceClassName: 'border-amber-300/60 bg-amber-500/10 text-amber-800 dark:text-amber-200',
  },
  critical: {
    dotClassName: 'bg-red-500',
    label: 'Crítico',
    surfaceClassName: 'border-red-300/60 bg-red-500/10 text-red-800 dark:text-red-200',
  },
  'on-track': {
    dotClassName: 'bg-emerald-500',
    label: 'No ritmo',
    surfaceClassName: 'border-emerald-300/60 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200',
  },
};

function compareReferenceMonth(referenceMonth: string, date: Date): number {
  const currentMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  return referenceMonth.localeCompare(currentMonth);
}

function countElapsedWeekdays(referenceMonth: string, date: Date): number {
  const [year, month] = referenceMonth.split('-').map(Number);
  if (!year || !month) return 0;

  const monthComparison = compareReferenceMonth(referenceMonth, date);
  if (monthComparison > 0) return 0;

  const monthLastDay = new Date(year, month, 0).getDate();
  const finalDay = monthComparison < 0 ? monthLastDay : Math.min(date.getDate(), monthLastDay);
  let weekdays = 0;

  for (let day = 1; day <= finalDay; day += 1) {
    const weekDay = new Date(year, month - 1, day).getDay();
    if (weekDay !== 0 && weekDay !== 6) weekdays += 1;
  }

  return weekdays;
}

export function calculateGoalPace({
  actual,
  referenceMonth,
  target,
  totalWorkingDays,
  today = new Date(),
}: {
  actual: number;
  referenceMonth: string;
  target: number;
  totalWorkingDays: number;
  today?: Date;
}): GoalPace | null {
  if (target <= 0 || totalWorkingDays <= 0) return null;

  const elapsedWorkingDays = Math.min(
    totalWorkingDays,
    countElapsedWeekdays(referenceMonth, today)
  );
  const expectedPercent = Math.min(100, (elapsedWorkingDays / totalWorkingDays) * 100);
  const actualPercent = Math.max(0, (actual / target) * 100);
  const expectedValueRaw = target * (expectedPercent / 100);
  const expectedValue = Math.round(expectedValueRaw);
  const deficitPercentagePoints = expectedPercent - actualPercent;
  const status: GoalPaceStatus = deficitPercentagePoints <= 5
    ? 'on-track'
    : deficitPercentagePoints <= 15
      ? 'attention'
      : 'critical';

  return {
    actualPercent,
    differenceFromExpectedPct: expectedValueRaw > 0
      ? ((actual - expectedValueRaw) / expectedValueRaw) * 100
      : null,
    elapsedWorkingDays,
    expectedPercent,
    expectedValue,
    remaining: Math.max(0, target - actual),
    status,
    totalWorkingDays,
  };
}

export function GoalPaceBadge({
  compact = false,
  pace,
}: {
  compact?: boolean;
  pace: GoalPace;
}) {
  const config = paceStatusConfig[pace.status];

  return (
    <span
      aria-label={`Ritmo da meta: ${config.label}`}
      className={classNames(
        'inline-flex w-fit shrink-0 items-center rounded-full border font-instrument font-semibold',
        compact ? 'gap-1 px-2 py-0.5 text-[10px]' : 'gap-2 px-3 py-1 text-xs',
        config.surfaceClassName
      )}
    >
      <span
        aria-hidden
        className={classNames(
          'shrink-0 rounded-full',
          compact ? 'h-1.5 w-1.5' : 'h-2 w-2',
          config.dotClassName
        )}
      />
      {config.label}
    </span>
  );
}

